import { QueryFilterGroupBy } from '../../shared/state/query-filter.functions';
import { get } from 'dot-prop';
import { isArray } from '@datorama/akita';

// TODO: fix return type (will differ depending on groupBy and returnAsObject properties)
/** accumulateByGroup - accumulate entities by group
 * @deprecated Use accumulateByGroupV2 instead.
 * @param entities - entities to accumulate
 * @param accumulatorFunction - function used to accumulate entities
 * @param groupBy - optionally group entities by a key e.g. 'transactionDateTime' and an optional value modifier e.g. format(value, 'yyyy-MM')
 * @param useValueFromPreviousGroup - use value from previous group if no value is found for current group
 * @param returnAsObject - return groups as object
 * @returns Observable<Array<R>> - array of accumulated entities
 */
export const accumulateByGroup = <T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy
    ) => R,
    groupBy?: QueryFilterGroupBy | Array<QueryFilterGroupBy>,
    useValueFromPreviousGroup = false,
    returnAsObject = true
) => {
    if (!entities) {
        return [];
        //throw new Error('No entities provided');
    }
    if (groupBy && !isArray(groupBy)) {
        // create placeholder for groups
        const GROUPS: { [group: string | number]: R & { entities?: Array<T> } } = {};
        for (let i = 0; i < entities.length; i++) {
            const ENTITY = entities[i];
            let previousGroup;
            let group = get<string | number>(entities[i], groupBy.key);
            // if groupBy.valueModifier is provided...
            if (groupBy.valueModifier) {
                // ...use it to modify the group value
                group = groupBy.valueModifier(group, entities[i]);
            }

            if (useValueFromPreviousGroup && Object.keys(GROUPS).length > 0) {
                previousGroup = get<string | number>(entities[i - 1], groupBy.key);
                if (groupBy.valueModifier && previousGroup) {
                    previousGroup = groupBy.valueModifier(previousGroup, entities[i - 1]);
                }
            }
            // add group and entities
            // @ts-ignore
            GROUPS[group] = {
                ...accumulatorFunction(
                    ENTITY,
                    GROUPS[group] ||
                        (useValueFromPreviousGroup ? GROUPS[previousGroup] : undefined),
                    i,
                    groupBy.subgroup
                ),
                entities: [...(GROUPS[group]?.entities || []), ENTITY]
            };
        }
        if (groupBy.subgroup) {
            Object.entries(GROUPS).forEach(([key, value]) => {
                const subgroups = accumulateByGroup(
                    value.entities,
                    accumulatorFunction,
                    groupBy.subgroup
                );
                GROUPS[key] = { ...GROUPS[key], subgroups };
            });
        }
        if (returnAsObject === true) {
            return GROUPS;
        } else if (returnAsObject === false) {
            return Object.values(GROUPS) as Array<R>;
        }
    } else if (groupBy && isArray(groupBy)) {
        // create placeholder for groups
        const GROUPS: {
            [groupName: string]: {
                [groupValue: string]: {
                    accumulated: R;
                    entities: T[];
                    subgroups?: any;
                };
            };
        } = {};
        groupBy.forEach((group) => {
            GROUPS[group.name] = {};
        });
        for (let i = 0; i < entities.length; i++) {
            const ENTITY = entities[i];
            groupBy.forEach((group) => {
                let groupValue = get<string | number>(entities[i], group.key);
                // if groupBy.valueModifier is provided...
                if (group.valueModifier) {
                    // ...use it to modify the group value
                    groupValue = group.valueModifier(groupValue, entities[i]);
                }
                // add group
                GROUPS[group.name][groupValue] = {
                    accumulated: accumulatorFunction(
                        ENTITY,
                        GROUPS[group.name][groupValue]?.accumulated,
                        i,
                        group.subgroup
                    ),
                    entities: [
                        ...(GROUPS[group.name][groupValue]?.entities || []),
                        ENTITY
                    ]
                };
            });
        }

        groupBy
            .filter((group) => group.subgroup)
            .forEach((group) => {
                Object.entries(GROUPS[group.name]).forEach(([key, groupData]) => {
                    const subgroups = accumulateByGroup(
                        groupData.entities,
                        accumulatorFunction,
                        group.subgroup
                    );
                    GROUPS[group.name][key] = {
                        ...groupData,
                        subgroups
                    };
                });
            });
        if (returnAsObject) {
            return GROUPS;
        } else {
            // flatten to array of accumulated results only
            return groupBy
                .map((group) =>
                    Object.values(GROUPS[group.name]).map((gd) => gd.accumulated)
                )
                .flat() as Array<R>;
        }
    } else {
        let accumulated: R;
        for (let i = 0; i < entities.length; i++) {
            const ENTITY = entities[i];
            accumulated = accumulatorFunction(ENTITY, accumulated, i);
        }
        return accumulated;
    }
};

/**
 * accumulateByGroupV2 - Accumulate entities by group with support for nested grouping and carry forward values.
 * @param entities - Array of entities to accumulate.
 * @param accumulatorFunction - Function to accumulate each entity.
 * @param groupBy - Grouping criteria, can be a single QueryFilterGroupBy or an array of them.
 * @param useValueFromPreviousGroup - Whether to carry forward the value from the previous group if current group value is null.
 * @param returnAsObject - Whether to return the result as an object or an array.
 * @param total - Optional total value to use in accumulation, total for group will be passed if omitted.
 * @returns Accumulated result based on grouping and accumulation logic.
 */
export function accumulateByGroupV2<T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy,
        total?: R
    ) => R,
    groupBy: undefined,
    useValueFromPreviousGroup?: boolean,
    returnAsObject?: boolean,
    total?: R
): R;
export function accumulateByGroupV2<T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy,
        total?: R
    ) => R,
    groupBy: QueryFilterGroupBy,
    useValueFromPreviousGroup: boolean,
    returnAsObject: boolean,
    total?: R
):
    | Record<string, { accumulated: R; entities: T[]; parentTotal?: R; subgroups?: any }>
    | R[];
export function accumulateByGroupV2<T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy,
        total?: R
    ) => R,
    groupBy: QueryFilterGroupBy,
    useValueFromPreviousGroup: boolean,
    returnAsObject: false,
    total?: R
): R[];
export function accumulateByGroupV2<T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy,
        total?: R
    ) => R,
    groupBy: Array<QueryFilterGroupBy>,
    useValueFromPreviousGroup?: boolean,
    returnAsObject?: boolean,
    total?: R
): Record<string, any>;
export function accumulateByGroupV2<T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy,
        total?: R
    ) => R,
    groupBy?: QueryFilterGroupBy | Array<QueryFilterGroupBy>,
    useValueFromPreviousGroup?: boolean,
    returnAsObject?: boolean,
    total?: R
): any {
    // Default returnAsObject to true when undefined
    const asObject = returnAsObject ?? true;
    const carryForward = useValueFromPreviousGroup ?? false;

    if (!entities) {
        throw new Error('No entities provided');
    }

    if (!total) {
        total = getTotal<T, R>(entities, accumulatorFunction);
    }

    // if no grouping is specified, just accumulate all entities
    if (!groupBy) {
        let accumulated: R | undefined;
        entities.forEach((entity, idx) => {
            accumulated = accumulatorFunction(
                entity,
                accumulated as R,
                idx,
                undefined,
                total
            );
        });
        return accumulated as R;
    }

    // if group by is an array...
    if (Array.isArray(groupBy)) {
        // ...accumulate by each group in parallel
        const parallel: Record<string, any> = {};
        for (const gb of groupBy) {
            parallel[gb.name!] = accumulateByGroupV2(
                entities,
                accumulatorFunction,
                gb,
                carryForward,
                asObject
            );
        }
        return parallel;
    }

    // normalize single groupBy into array to reuse code
    const groupsArray = [groupBy];

    type GroupBucket = {
        accumulated: R;
        entities: T[];
        parentTotal?: R;
        subgroups?: Record<string, GroupBucket>;
    };

    const rootBuckets: Record<string, GroupBucket> = {};

    entities.forEach((entity, idx) => {
        let currentLevel = rootBuckets;

        for (const gb of groupsArray) {
            // get raw key
            let rawKey = get(entity, gb.key) as string | number;
            if (gb.valueModifier) {
                rawKey = gb.valueModifier(rawKey, entity) as string | number;
            }
            // carry forward if needed
            let keyValue = rawKey;
            if (carryForward && keyValue == null && idx > 0) {
                let prevRaw = get(entities[idx - 1], gb.key) as string | number;
                if (gb.valueModifier) {
                    prevRaw = gb.valueModifier(prevRaw, entities[idx - 1]) as
                        | string
                        | number;
                }
                keyValue = prevRaw;
            }
            const key = String(keyValue);

            // init bucket
            if (!currentLevel[key]) {
                currentLevel[key] = {
                    accumulated: undefined as any,
                    entities: [],
                    subgroups: {}
                };
            }
            const bucket = currentLevel[key]!;

            // accumulate and record entity
            bucket.accumulated = accumulatorFunction(
                entity,
                bucket.accumulated,
                idx,
                gb.subgroup,
                bucket.parentTotal || total
            );
            bucket.entities.push(entity);

            // descend into subgroup level for next iteration
            currentLevel = bucket.subgroups!;
        }
    });

    // if groupBy is not an array and has a subgroup...
    if (!Array.isArray(groupBy) && groupBy.subgroup) {
        // ...for each first‐level bucket, build its subgroups
        for (const [key, bucket] of Object.entries(rootBuckets)) {
            bucket.subgroups = accumulateByGroupV2(
                bucket.entities,
                accumulatorFunction,
                groupBy.subgroup, // the nested group definition
                carryForward, // reuse useValueFromPreviousGroup flag
                asObject, // reuse returnAsObject flag
                bucket.accumulated
            ) as any;
        }
    }

    // if returnAsObject is true, return the root buckets as an object
    if (asObject) {
        return rootBuckets;
    } else {
        // ...else flatten one level of accumulated values and return as an array
        return Object.values(rootBuckets).map((b) => b.accumulated) as R[];
    }
}

/**
 * Compute totals for each group in a grouped result.
 *
 * @param buckets The grouped buckets returned by accumulateByGroupV2
 * @param selector A function to extract a numeric value from the accumulated result
 * @returns An object mapping each group key to its total numeric value
 */
export function getGroupTotals<T, R>(
    buckets: Record<string, { accumulated: R; entities: T[]; subgroups?: any }>,
    selector: (acc: R) => number
): Record<string, number> {
    const totals: Record<string, number> = {};
    for (const [key, bucket] of Object.entries(buckets)) {
        totals[key] = selector(bucket.accumulated);
    }
    return totals;
}

export function getTotal<T, R>(
    entities: Array<T>,
    accumulatorFunction: (
        entity: T,
        acc: R,
        index: number,
        subgroup?: QueryFilterGroupBy,
        total?: R
    ) => R
) {
    let accumulated: R | undefined;
    entities.forEach((entity, idx) => {
        accumulated = accumulatorFunction(entity, accumulated as R, idx);
    });
    return accumulated as R;
}

interface AggregatedNode<T> {
    accumulated: T;
    entities: any[];
    subgroups: Record<string, AggregatedNode<T>>;
}

interface MergedNode<P, Q> {
    accumulatedPosition: P;
    accumulatedPerformance: Q;
    entitiesPosition: any[];
    entitiesPerformance: any[];
    subgroups: Record<string, MergedNode<P, Q>>;
}

/**
 * One level deeper: groups map to inner maps of AggregatedNode
 */
export interface DoubleTree<T> {
    entities: any[];
    groups: Record<string, Record<string, AggregatedNode<T>>>;
}

/**
 * Recursively merge two AggregatedNode trees (unlimited depth).
 */
function mergeNode<P, Q>(
    posNode: AggregatedNode<P> | undefined,
    perfNode: AggregatedNode<Q> | undefined
): MergedNode<P, Q> {
    const entitiesPosition = posNode?.entities ?? [];
    const entitiesPerformance = perfNode?.entities ?? [];
    const accumulatedPosition = posNode?.accumulated as P;
    const accumulatedPerformance = perfNode?.accumulated as Q;

    // union of next‐level subgroup keys
    const childKeys = new Set<string>([
        ...Object.keys(posNode?.subgroups ?? {}),
        ...Object.keys(perfNode?.subgroups ?? {})
    ]);

    const subgroups: Record<string, MergedNode<P, Q>> = {};
    for (const k of childKeys) {
        subgroups[k] = mergeNode(posNode?.subgroups[k], perfNode?.subgroups[k]);
    }

    return {
        entitiesPosition,
        entitiesPerformance,
        accumulatedPosition,
        accumulatedPerformance,
        subgroups
    };
}

/**
 * Merge two “double‐tree” wrappers (outer groups → inner groups → nodes).
 */
export function mergeAggregatedTrees<P, Q>(
    posTree: DoubleTree<P>,
    perfTree: DoubleTree<Q>
): MergedDoubleTree<P, Q> {
    // top‐level union of outer group keys
    const outerKeys = new Set<string>([
        ...Object.keys(posTree.groups),
        ...Object.keys(perfTree.groups)
    ]);

    const mergedGroups: Record<string, Record<string, MergedNode<P, Q>>> = {};

    for (const outerKey of outerKeys) {
        const posInner = posTree.groups[outerKey] ?? {};
        const perfInner = perfTree.groups[outerKey] ?? {};

        // union of inner group keys
        const innerKeys = new Set<string>([
            ...Object.keys(posInner),
            ...Object.keys(perfInner)
        ]);

        const mergedInner: Record<string, MergedNode<P, Q>> = {};
        for (const innerKey of innerKeys) {
            mergedInner[innerKey] = mergeNode(posInner[innerKey], perfInner[innerKey]);
        }

        mergedGroups[outerKey] = mergedInner;
    }

    return {
        entitiesPosition: posTree.entities,
        entitiesPerformance: perfTree.entities,
        groups: mergedGroups
    };
}

/**
 * The merged double‐tree shape.
 */
export interface MergedDoubleTree<P, Q> {
    entitiesPosition: any[];
    entitiesPerformance: any[];
    groups: Record<string, Record<string, MergedNode<P, Q>>>;
}
